import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { GraduationCapIcon, CarIcon, DumbbellIcon, BriefcaseIcon, StarIcon, BookOpenIcon, WrenchIcon, HeartIcon, DollarSignIcon } from 'lucide-react';
interface Player {
  id: string;
  name: string;
  color: string;
  pathIndex: number | null;
  avatar?: string | null;
}
interface JourneyStartSceneProps {
  players: Player[];
  onStartPlaying: () => void;
}
const PATH_COLORS = [{
  stroke: '#8b5cf6',
  fill: '#a78bfa',
  glow: 'rgba(139,92,246,0.9)',
  name: 'School',
  emoji: '🎓',
  tileColor: '#e9d5ff',
  laneColor: '#a855f7'
}, {
  stroke: '#ec4899',
  fill: '#f472b6',
  glow: 'rgba(236,72,153,0.9)',
  name: 'Work',
  emoji: '💼',
  tileColor: '#fce7f3',
  laneColor: '#ec4899'
}, {
  stroke: '#f59e0b',
  fill: '#fbbf24',
  glow: 'rgba(245,158,11,0.9)',
  name: 'Trade',
  emoji: '🔧',
  tileColor: '#fed7aa',
  laneColor: '#f97316'
}, {
  stroke: '#14b8a6',
  fill: '#2dd4bf',
  glow: 'rgba(20,184,166,0.9)',
  name: 'Self-Made',
  emoji: '✨',
  tileColor: '#ccfbf1',
  laneColor: '#14b8a6'
}];
// 4 lanes × 4 tiles per lane, ordered far → near
const LANE_TILES: Array<Array<{
  icon: ComponentType<any>;
  text: string;
  amount: string;
}>> = [
// Lane 0 — School (purple)
[{
  icon: BookOpenIcon,
  text: 'Pulled an\nAll-Nighter',
  amount: '+$50'
}, {
  icon: BookOpenIcon,
  text: 'Aced\nthe Exam',
  amount: '+$100'
}, {
  icon: BookOpenIcon,
  text: 'Kid Needed\nSchool Supplies',
  amount: '-$80'
}, {
  icon: GraduationCapIcon,
  text: 'Study Hard\nPay Off',
  amount: '+$150'
}],
// Lane 1 — Work (pink)
[{
  icon: BriefcaseIcon,
  text: 'Missed\nthe Bus',
  amount: '-$30'
}, {
  icon: BriefcaseIcon,
  text: 'No Income\nThis Week',
  amount: '-$100'
}, {
  icon: BriefcaseIcon,
  text: 'Got a\nPart-Time Job',
  amount: '+$120'
}, {
  icon: BriefcaseIcon,
  text: 'Promotion\nat Work',
  amount: '+$200'
}],
// Lane 2 — Trade (orange)
[{
  icon: WrenchIcon,
  text: 'Tools Need\nUpgrade',
  amount: '-$60'
}, {
  icon: WrenchIcon,
  text: 'Big Job\nCame In',
  amount: '+$180'
}, {
  icon: CarIcon,
  text: 'Car Broke\nDown',
  amount: '-$200'
}, {
  icon: DollarSignIcon,
  text: 'Side Hustle\nSuccess',
  amount: '+$300'
}],
// Lane 3 — Self-Made (teal)
[{
  icon: StarIcon,
  text: 'Compliment\nfrom Stranger',
  amount: '+$0'
}, {
  icon: HeartIcon,
  text: 'Self-Care\nDay',
  amount: '+$40'
}, {
  icon: DumbbellIcon,
  text: 'Invest in\nYourself',
  amount: '+$250'
}, {
  icon: StarIcon,
  text: 'Manifested\nIt',
  amount: '+$400'
}]];
export function JourneyStartScene({
  players,
  onStartPlaying
}: JourneyStartSceneProps) {
  const playersByPath: Record<number, Player[]> = {
    0: [],
    1: [],
    2: [],
    3: []
  };
  players.forEach(p => {
    const idx = p.pathIndex !== null && p.pathIndex !== undefined ? Math.max(0, Math.min(3, p.pathIndex)) : 0;
    playersByPath[idx].push(p);
  });
  return <div className="fixed inset-0 w-full h-full z-30 overflow-hidden" style={{
    background: 'linear-gradient(to bottom, #6BB6E8 0%, #8FD0F0 35%, #B8DEF0 55%, #4a3550 75%, #2a1a3a 100%)'
  }} data-id="element-194">
      {/* ===== SKY: clouds ===== */}
      <div className="absolute top-[6%] left-[8%] w-40 h-12 bg-white/80 rounded-full blur-md" data-id="element-195" />
      <div className="absolute top-[10%] right-[12%] w-48 h-14 bg-white/70 rounded-full blur-md" data-id="element-196" />
      <div className="absolute top-[16%] left-[40%] w-36 h-10 bg-white/60 rounded-full blur-md" data-id="element-197" />

      {/* ===== CITYSCAPE BACKDROP — buildings silhouette ===== */}
      <div className="absolute inset-x-0 top-0 h-[62%] pointer-events-none" data-id="element-198">
        {/* Far skyline */}
        <div className="absolute bottom-0 left-0 right-0 h-[55%] flex items-end justify-center gap-1 opacity-90" data-id="element-199">
          {[{
          h: 45,
          w: 8,
          c: '#3a4a5e'
        }, {
          h: 70,
          w: 6,
          c: '#2d3a4d'
        }, {
          h: 55,
          w: 7,
          c: '#445566'
        }, {
          h: 85,
          w: 9,
          c: '#2a3645'
        }, {
          h: 60,
          w: 5,
          c: '#3e4d5f'
        }, {
          h: 95,
          w: 10,
          c: '#1f2a38'
        }, {
          h: 75,
          w: 7,
          c: '#36465a'
        }, {
          h: 100,
          w: 8,
          c: '#28344a'
        }, {
          h: 65,
          w: 6,
          c: '#3d4d62'
        }, {
          h: 80,
          w: 9,
          c: '#2a3848'
        }, {
          h: 55,
          w: 7,
          c: '#445870'
        }, {
          h: 90,
          w: 8,
          c: '#1e2a3a'
        }, {
          h: 70,
          w: 6,
          c: '#384858'
        }, {
          h: 85,
          w: 9,
          c: '#2c3a4c'
        }, {
          h: 50,
          w: 7,
          c: '#46586c'
        }, {
          h: 95,
          w: 10,
          c: '#202d3e'
        }].map((b, i) => <div key={i} style={{
          height: `${b.h}%`,
          width: `${b.w}%`,
          background: `linear-gradient(to top, ${b.c} 0%, ${b.c}dd 100%)`
        }} className="relative shadow-2xl" data-id="element-200">
              {/* lit windows */}
              <div className="absolute inset-2 grid grid-cols-3 gap-[3px] opacity-80" data-id="element-201">
                {[...Array(Math.floor(b.h / 8))].map((_, j) => <div key={j} className="bg-yellow-200/70 rounded-[1px]" style={{
              height: '4px',
              opacity: Math.random() > 0.4 ? 0.9 : 0.2
            }} data-id="element-202" />)}
              </div>
            </div>)}
        </div>
      </div>

      {/* ===== LEFT BIG NEON BILLBOARD (TRIPPIN' THROUGH LIFE) ===== */}
      <motion.div initial={{
      opacity: 0,
      x: -30
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.8
    }} className="absolute top-[4%] left-[2%] w-[26%] max-w-[380px] z-10" data-id="element-203">
        <div className="relative rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(236,72,153,0.6)] border-4 border-pink-300/40" style={{
        background: 'linear-gradient(135deg, #4c1d95 0%, #831843 50%, #6b21a8 100%)',
        aspectRatio: '16/9'
      }} data-id="element-204">
          {/* sparkle bg */}
          {[...Array(12)].map((_, i) => <motion.div key={i} className="absolute text-yellow-200 text-xs" style={{
          top: `${Math.random() * 90}%`,
          left: `${Math.random() * 90}%`
        }} animate={{
          opacity: [0.3, 1, 0.3],
          scale: [0.8, 1.2, 0.8]
        }} transition={{
          duration: 1.5 + Math.random(),
          repeat: Infinity,
          delay: Math.random() * 2
        }} data-id="element-205">
              ✦
            </motion.div>)}
          <div className="relative p-4 h-full flex flex-col justify-center items-center text-center" data-id="element-206">
            <h2 className="text-3xl md:text-4xl font-bold leading-none mb-1" style={{
            fontFamily: '"Dancing Script", cursive',
            background: 'linear-gradient(180deg, #fef08a 0%, #f472b6 50%, #c026d3 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 30px rgba(236,72,153,0.6)',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
          }} data-id="element-207">
              Trippin'
            </h2>
            <h2 className="text-2xl md:text-3xl font-bold leading-none mb-1" style={{
            fontFamily: '"Dancing Script", cursive',
            background: 'linear-gradient(180deg, #fef08a 0%, #f59e0b 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
          }} data-id="element-208">
              Through Life
            </h2>
            <p className="text-sm md:text-base font-bold text-white" style={{
            fontFamily: '"Dancing Script", cursive'
          }} data-id="element-209">
              with Mandy
            </p>
            <div className="mt-2 px-4 py-1 rounded-full bg-pink-500 text-white text-[10px] md:text-xs font-bold uppercase tracking-wider shadow-lg" data-id="element-210">
              Live Your Story
            </div>
          </div>
        </div>
        {/* ticker below */}
        <div className="mt-1 bg-black/80 border border-pink-400/60 rounded-lg py-1.5 px-3 text-[9px] md:text-[10px] text-pink-200 font-bold tracking-wider text-center" data-id="element-211">
          🎲 ROLL THE DICE • MAKE CHOICES • BUILD YOUR LIFE 🎲
        </div>
      </motion.div>

      {/* ===== CENTER TOWER BILLBOARD ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.2
    }} className="absolute top-[3%] left-1/2 -translate-x-1/2 w-[14%] max-w-[160px] z-10" data-id="element-212">
        <div className="rounded-t-xl overflow-hidden shadow-2xl border-2 border-white/20" data-id="element-213">
          <div className="bg-gradient-to-r from-pink-500 to-pink-600 text-white text-[9px] md:text-xs font-bold py-2 text-center leading-tight" data-id="element-214">
            TRIPPIN'
            <br data-id="element-215" />
            THROUGH LIFE
          </div>
          <div className="bg-gradient-to-r from-teal-400 to-teal-500 text-white text-[9px] md:text-xs font-bold py-3 text-center leading-tight flex flex-col items-center" data-id="element-216">
            LIVE YOUR
            <br data-id="element-217" />
            LIFE
            <span className="text-base" data-id="element-218">🎲</span>
          </div>
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white text-[9px] md:text-xs font-bold py-3 text-center leading-tight flex flex-col items-center" data-id="element-219">
            MAKE
            <br data-id="element-220" />
            CHOICES
            <HeartIcon className="w-3 h-3 fill-white" data-id="element-221" />
          </div>
          <div className="bg-gradient-to-r from-orange-400 to-orange-500 text-white text-[9px] md:text-xs font-bold py-3 text-center leading-tight flex flex-col items-center" data-id="element-222">
            BUILD YOUR
            <br data-id="element-223" />
            FUTURE
            <StarIcon className="w-3 h-3 fill-white" data-id="element-224" />
          </div>
        </div>
      </motion.div>

      {/* ===== RIGHT BILLBOARDS ===== */}
      <motion.div initial={{
      opacity: 0,
      x: 30
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.8
    }} className="absolute top-[5%] right-[3%] w-[22%] max-w-[300px] flex flex-col gap-2 z-10" data-id="element-225">
        {/* Manifest Your Future */}
        <div className="rounded-xl overflow-hidden shadow-2xl border-2 border-purple-400/40 p-3" style={{
        background: 'linear-gradient(135deg, #581c87 0%, #be185d 100%)',
        boxShadow: '0 0 40px rgba(168,85,247,0.5)'
      }} data-id="element-226">
          <p className="text-white font-bold text-center leading-tight text-base md:text-xl" style={{
          textShadow: '0 0 12px rgba(255,255,255,0.5)'
        }} data-id="element-227">
            MANIFEST
            <br data-id="element-228" />
            YOUR
            <br data-id="element-229" />
            FUTURE
          </p>
        </div>
        {/* Level Up */}
        <div className="rounded-xl overflow-hidden shadow-2xl border-2 border-pink-300/50 p-3" style={{
        background: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
        boxShadow: '0 0 40px rgba(236,72,153,0.6)'
      }} data-id="element-230">
          <p className="text-white font-bold text-center leading-tight text-sm md:text-lg" data-id="element-231">
            LEVEL UP
            <br data-id="element-232" />
            EVERY DAY ↗
          </p>
        </div>
        {/* Good Things Take Time */}
        <div className="self-end rounded-full px-4 py-2 bg-pink-500 border-2 border-pink-300 shadow-[0_0_30px_rgba(236,72,153,0.7)]" data-id="element-233">
          <p className="text-white font-bold text-[10px] md:text-xs text-center leading-tight" data-id="element-234">
            GOOD
            <br data-id="element-235" />
            THINGS
            <br data-id="element-236" />
            TAKE TIME
          </p>
        </div>
      </motion.div>

      {/* ===== CHERRY BLOSSOM BRANCHES — top corners ===== */}
      <CherryBranch className="absolute top-0 left-0 w-[28%] h-[40%]" data-id="element-237" />
      <CherryBranch flip className="absolute top-0 right-0 w-[28%] h-[40%]" data-id="element-238" />

      {/* ===== 3D PERSPECTIVE ROAD ===== */}
      <div className="absolute inset-x-0 bottom-0 h-[62%]" style={{
      perspective: '1000px',
      perspectiveOrigin: '50% 0%'
    }} data-id="element-239">
        <div className="absolute inset-0" style={{
        transformStyle: 'preserve-3d',
        transform: 'rotateX(58deg)',
        transformOrigin: '50% 0%'
      }} data-id="element-240">
          {/* Asphalt base */}
          <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, #1a1520 0%, #2a2030 50%, #3a2840 100%)'
        }} data-id="element-241" />

          {/* Road plane: 4 lanes side-by-side */}
          <div className="absolute inset-x-[5%] inset-y-0 flex" data-id="element-242">
            {PATH_COLORS.map((path, laneIdx) => <div key={laneIdx} className="relative flex-1 flex flex-col justify-end pb-8" style={{
            background: `linear-gradient(to top, ${path.laneColor}aa 0%, ${path.laneColor}66 50%, ${path.laneColor}22 100%)`,
            borderLeft: laneIdx === 0 ? `3px solid ${path.glow}` : `2px dashed rgba(255,255,255,0.5)`,
            borderRight: laneIdx === 3 ? `3px solid ${path.glow}` : 'none',
            boxShadow: `inset 0 0 60px ${path.glow}`
          }} data-id="element-243">
                {/* Glow strip on edges */}
                <div className="absolute inset-y-0 left-0 w-1" style={{
              background: path.glow,
              boxShadow: `0 0 20px ${path.glow}, 0 0 40px ${path.glow}`
            }} data-id="element-244" />
                {laneIdx === 3 && <div className="absolute inset-y-0 right-0 w-1" style={{
              background: path.glow,
              boxShadow: `0 0 20px ${path.glow}, 0 0 40px ${path.glow}`
            }} data-id="element-245" />}

                {/* Tiles stacked far→near (top→bottom) */}
                <div className="relative h-full flex flex-col justify-end gap-3 px-2 pb-12" data-id="element-246">
                  {LANE_TILES[laneIdx].map((tile, tileIdx) => {
                const Icon = tile.icon;
                const isPositive = tile.amount.startsWith('+');
                // far-to-near: tileIdx 0 = farthest (smallest), 3 = nearest (biggest)
                const scale = 0.35 + tileIdx * 0.22;
                return <motion.div key={tileIdx} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.4 + tileIdx * 0.1
                }} className="rounded-2xl shadow-2xl flex flex-col items-center justify-center text-center relative overflow-hidden" style={{
                  background: path.tileColor,
                  border: `${Math.max(2, scale * 5)}px solid ${path.stroke}`,
                  padding: `${scale * 16}px ${scale * 8}px`,
                  minHeight: `${scale * 140}px`,
                  boxShadow: `0 ${scale * 10}px ${scale * 20}px rgba(0,0,0,0.4), 0 0 ${scale * 30}px ${path.glow}`
                }} data-id="element-247">
                        {/* glossy top */}
                        <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" data-id="element-248" />
                        <Icon style={{
                    width: `${scale * 36}px`,
                    height: `${scale * 36}px`,
                    color: path.stroke
                  }} className="mb-1 relative z-10" strokeWidth={2.5} data-id="element-249" />
                        <p className="font-bold text-gray-900 leading-tight whitespace-pre-line relative z-10" style={{
                    fontSize: `${Math.max(8, scale * 14)}px`
                  }} data-id="element-250">
                          {tile.text}
                        </p>
                        <p className="font-extrabold relative z-10 mt-1" style={{
                    fontSize: `${Math.max(9, scale * 18)}px`,
                    color: isPositive ? '#15803d' : '#b91c1c'
                  }} data-id="element-251">
                          {tile.amount}
                        </p>
                      </motion.div>;
              })}
                </div>

                {/* Player avatars at bottom of lane */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-1 -mb-4" style={{
              transform: 'translateX(-50%) rotateX(-58deg)',
              transformOrigin: 'bottom center'
            }} data-id="element-252">
                  {playersByPath[laneIdx].map((p, i) => <motion.div key={p.id} initial={{
                y: -10,
                opacity: 0
              }} animate={{
                y: [0, -4, 0],
                opacity: 1
              }} transition={{
                y: {
                  duration: 1.6,
                  repeat: Infinity,
                  delay: i * 0.15
                },
                opacity: {
                  duration: 0.4,
                  delay: 0.6 + i * 0.1
                }
              }} className="flex flex-col items-center" data-id="element-253">
                      <div className="rounded-full border-2 border-white shadow-2xl overflow-hidden flex items-center justify-center" style={{
                  width: 36,
                  height: 36,
                  background: path.fill,
                  boxShadow: `0 0 16px ${path.glow}`
                }} data-id="element-254">
                        {p.avatar ? <img src={p.avatar} alt="" className="w-full h-full object-cover" data-id="element-255" /> : <span className="text-white font-bold text-sm" data-id="element-256">
                            {p.name.charAt(0).toUpperCase()}
                          </span>}
                      </div>
                      <div className="mt-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold text-white whitespace-nowrap" style={{
                  background: path.stroke
                }} data-id="element-257">
                        {p.name}
                      </div>
                    </motion.div>)}
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* ===== 3D DICE — left foreground (NOT rotated with road) ===== */}
      <motion.div initial={{
      scale: 0,
      rotate: -180
    }} animate={{
      scale: 1,
      rotate: 0
    }} transition={{
      type: 'spring',
      stiffness: 100,
      delay: 0.6
    }} className="absolute bottom-[14%] left-[18%] z-20" data-id="element-258">
        <motion.div animate={{
        y: [0, -8, 0],
        rotate: [-3, 3, -3]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="relative" style={{
        width: 110,
        height: 110
      }} data-id="element-259">
          {/* Dice cube via box-shadow stack */}
          <div className="absolute inset-0 rounded-2xl" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 50%, #d4d4d4 100%)',
          transform: 'perspective(400px) rotateX(-12deg) rotateY(-22deg)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.5), inset -8px -8px 20px rgba(0,0,0,0.15), inset 8px 8px 20px rgba(255,255,255,0.8)'
        }} data-id="element-260">
            {/* face dots — showing 5 */}
            <div className="absolute inset-4 grid grid-cols-3 grid-rows-3 gap-1" data-id="element-261">
              <div className="w-3 h-3 rounded-full bg-gray-900 shadow-inner" data-id="element-262" />
              <div data-id="element-263" />
              <div className="w-3 h-3 rounded-full bg-gray-900 shadow-inner" data-id="element-264" />
              <div data-id="element-265" />
              <div className="w-3 h-3 rounded-full bg-gray-900 shadow-inner self-center justify-self-center" data-id="element-266" />
              <div data-id="element-267" />
              <div className="w-3 h-3 rounded-full bg-gray-900 shadow-inner" data-id="element-268" />
              <div data-id="element-269" />
              <div className="w-3 h-3 rounded-full bg-gray-900 shadow-inner" data-id="element-270" />
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* ===== 3D PURPLE PAWN — center foreground ===== */}
      <motion.div initial={{
      scale: 0,
      y: 50
    }} animate={{
      scale: 1,
      y: 0
    }} transition={{
      type: 'spring',
      stiffness: 80,
      delay: 0.5
    }} className="absolute bottom-[10%] left-1/2 -translate-x-1/2 z-20" data-id="element-271">
        <motion.div animate={{
        y: [0, -6, 0]
      }} transition={{
        duration: 2.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-272">
          <svg width="140" height="180" viewBox="0 0 140 180" data-id="element-273">
            <defs data-id="element-274">
              <radialGradient id="pawnBody" cx="35%" cy="30%" r="70%" data-id="element-275">
                <stop offset="0%" stopColor="#d8b4fe" data-id="element-276" />
                <stop offset="40%" stopColor="#a855f7" data-id="element-277" />
                <stop offset="80%" stopColor="#7c3aed" data-id="element-278" />
                <stop offset="100%" stopColor="#4c1d95" data-id="element-279" />
              </radialGradient>
              <radialGradient id="pawnHead" cx="35%" cy="30%" r="70%" data-id="element-280">
                <stop offset="0%" stopColor="#e9d5ff" data-id="element-281" />
                <stop offset="40%" stopColor="#c084fc" data-id="element-282" />
                <stop offset="100%" stopColor="#7c3aed" data-id="element-283" />
              </radialGradient>
              <filter id="pawnGlow" data-id="element-284">
                <feGaussianBlur stdDeviation="6" result="blur" data-id="element-285" />
                <feMerge data-id="element-286">
                  <feMergeNode in="blur" data-id="element-287" />
                  <feMergeNode in="SourceGraphic" data-id="element-288" />
                </feMerge>
              </filter>
              <ellipse id="pawnShadow" data-id="element-289" />
            </defs>
            {/* ground shadow */}
            <ellipse cx="70" cy="170" rx="45" ry="8" fill="rgba(0,0,0,0.5)" filter="url(#pawnGlow)" data-id="element-290" />
            {/* base disc */}
            <ellipse cx="70" cy="160" rx="42" ry="12" fill="url(#pawnBody)" data-id="element-291" />
            <ellipse cx="70" cy="156" rx="42" ry="10" fill="#7c3aed" data-id="element-292" />
            {/* body */}
            <path d="M 38 156 Q 40 110 55 85 L 85 85 Q 100 110 102 156 Z" fill="url(#pawnBody)" data-id="element-293" />
            {/* collar ring */}
            <ellipse cx="70" cy="85" rx="20" ry="6" fill="#5b21b6" data-id="element-294" />
            <ellipse cx="70" cy="83" rx="20" ry="5" fill="url(#pawnBody)" data-id="element-295" />
            {/* neck */}
            <path d="M 58 80 Q 56 65 60 55 L 80 55 Q 84 65 82 80 Z" fill="url(#pawnBody)" data-id="element-296" />
            {/* head ring */}
            <ellipse cx="70" cy="55" rx="14" ry="4" fill="#5b21b6" data-id="element-297" />
            {/* head sphere */}
            <circle cx="70" cy="38" r="22" fill="url(#pawnHead)" data-id="element-298" />
            {/* highlight */}
            <ellipse cx="62" cy="30" rx="6" ry="9" fill="white" opacity="0.5" data-id="element-299" />
            <ellipse cx="58" cy="120" rx="4" ry="20" fill="white" opacity="0.25" data-id="element-300" />
          </svg>
        </motion.div>
      </motion.div>

      {/* ===== CHOOSE YOUR PATH SIGN — left ===== */}
      <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.7
    }} className="absolute left-3 md:left-5 bottom-[34%] bg-black/95 backdrop-blur-sm rounded-xl p-3 md:p-4 shadow-2xl border-2 border-pink-400/60 z-20" style={{
      boxShadow: '0 0 30px rgba(236,72,153,0.4)'
    }} data-id="element-301">
        <h3 className="text-white font-extrabold text-xs md:text-sm mb-2 tracking-wide" data-id="element-302">
          CHOOSE
          <br data-id="element-303" />
          YOUR PATH
        </h3>
        <div className="space-y-1.5" data-id="element-304">
          {PATH_COLORS.map((path, i) => {
          const PathIcon = [GraduationCapIcon, BriefcaseIcon, WrenchIcon, StarIcon][i];
          return <div key={i} className="flex items-center gap-2 text-[10px] md:text-xs font-bold" data-id="element-305">
                <PathIcon className="w-3.5 h-3.5" style={{
              color: path.fill
            }} strokeWidth={2.5} data-id="element-306" />
                <span style={{
              color: path.fill
            }} data-id="element-307">
                  {path.name.toUpperCase()}
                </span>
              </div>;
        })}
        </div>
      </motion.div>

      {/* ===== TODAY'S CHOICES SIGN — right ===== */}
      <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.7
    }} className="absolute right-3 md:right-5 bottom-[34%] bg-black/95 backdrop-blur-sm rounded-xl p-3 md:p-4 shadow-2xl border-2 border-purple-400/60 z-20 max-w-[140px]" style={{
      boxShadow: '0 0 30px rgba(168,85,247,0.4)'
    }} data-id="element-308">
        <p className="text-white font-extrabold text-[10px] md:text-xs text-center leading-tight tracking-wide" data-id="element-309">
          TODAY'S
          <br data-id="element-310" />
          <span className="text-pink-300" data-id="element-311">CHOICES</span>
          <br data-id="element-312" />
          TOMORROW'S
          <br data-id="element-313" />
          <span className="text-purple-300" data-id="element-314">LIFE</span>
        </p>
      </motion.div>

      {/* ===== ROLL THE DICE CTA — bottom ===== */}
      <div className="absolute bottom-6 left-0 right-0 flex justify-center z-30" data-id="element-315">
        <motion.button onClick={onStartPlaying} initial={{
        scale: 0,
        y: 30
      }} animate={{
        scale: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 120,
        delay: 0.9
      }} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.97
      }} className="group relative flex items-center gap-3 md:gap-4 bg-gradient-to-r from-pink-500 via-pink-600 to-pink-500 px-4 md:px-6 py-2.5 md:py-3 rounded-full shadow-[0_0_50px_rgba(236,72,153,0.9)] hover:shadow-[0_0_70px_rgba(236,72,153,1)] transition-all border-2 border-pink-200/60" data-id="element-316">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center shadow-inner" data-id="element-317">
            <motion.span className="text-2xl md:text-3xl" animate={{
            rotate: [0, 360]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-318">
              🎲
            </motion.span>
          </div>
          <div className="flex flex-col items-start" data-id="element-319">
            <span className="text-white font-extrabold text-base md:text-xl uppercase tracking-wider leading-none" data-id="element-320">
              Roll the Dice
            </span>
            <span className="text-pink-100 text-[10px] md:text-xs font-medium" data-id="element-321">
              to start your journey!
            </span>
          </div>
        </motion.button>
      </div>

      {/* ===== Twinkling neon lights ===== */}
      {[...Array(30)].map((_, i) => <motion.div key={i} className="absolute rounded-full pointer-events-none" style={{
      top: `${5 + Math.random() * 50}%`,
      left: `${Math.random() * 100}%`,
      width: 2 + Math.random() * 2,
      height: 2 + Math.random() * 2,
      background: ['#fef08a', '#f472b6', '#a78bfa', '#5eead4'][i % 4],
      boxShadow: `0 0 6px currentColor`
    }} animate={{
      opacity: [0, 1, 0],
      scale: [0.8, 1.4, 0.8]
    }} transition={{
      duration: 1 + Math.random() * 2,
      repeat: Infinity,
      delay: Math.random() * 3
    }} data-id="element-322" />)}
    </div>;
}
// ===== Cherry blossom branch decoration =====
function CherryBranch({
  flip,
  className
}: {
  flip?: boolean;
  className?: string;
}) {
  return <svg viewBox="0 0 300 300" className={className} style={{
    transform: flip ? 'scaleX(-1)' : undefined,
    pointerEvents: 'none'
  }} data-id="element-323">
      {/* Branches */}
      <path d="M 0 0 Q 60 40 120 60 T 240 100" stroke="#5b3a1f" strokeWidth="5" fill="none" strokeLinecap="round" data-id="element-324" />
      <path d="M 30 20 Q 50 50 60 80" stroke="#5b3a1f" strokeWidth="3" fill="none" strokeLinecap="round" data-id="element-325" />
      <path d="M 100 50 Q 110 80 105 110" stroke="#5b3a1f" strokeWidth="3" fill="none" strokeLinecap="round" data-id="element-326" />
      <path d="M 180 80 Q 200 100 195 130" stroke="#5b3a1f" strokeWidth="3" fill="none" strokeLinecap="round" data-id="element-327" />
      {/* Blossoms — clusters */}
      {[[10, 5, 12], [40, 25, 10], [70, 15, 14], [55, 75, 11], [105, 110, 13], [120, 60, 12], [160, 75, 10], [195, 130, 14], [220, 95, 11], [240, 105, 13], [85, 95, 9], [25, 60, 11], [145, 30, 10], [180, 25, 12], [205, 50, 9]].map(([cx, cy, r], i) => <g key={i} data-id="element-328">
          {/* 5-petal flower */}
          {[0, 72, 144, 216, 288].map(angle => {
        const rad = angle * Math.PI / 180;
        const px = cx + Math.cos(rad) * (r * 0.55);
        const py = cy + Math.sin(rad) * (r * 0.55);
        return <circle key={angle} cx={px} cy={py} r={r * 0.55} fill={i % 3 === 0 ? '#fbcfe8' : i % 3 === 1 ? '#f9a8d4' : '#f472b6'} opacity="0.95" data-id="element-329" />;
      })}
          <circle cx={cx} cy={cy} r={r * 0.3} fill="#fde047" data-id="element-330" />
        </g>)}
    </svg>;
}